import { useTranslation } from 'react-i18next'

export const useFormatter = () => {
    const { t, i18n } = useTranslation();

    const dateformatter = Intl.DateTimeFormat(i18n.resolvedLanguage);
    const datetimeformatter = Intl.DateTimeFormat(i18n.resolvedLanguage, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false // Per il formato 24 ore
    })

    const distanceformatter = Intl.NumberFormat(i18n.resolvedLanguage, {
        style: 'unit',
        unit: 'kilometer',
        unitDisplay: 'short',
        maximumFractionDigits: 0
    });

    const numberformatter = Intl.NumberFormat(i18n.resolvedLanguage, {
        useGrouping: true,
        maximumFractionDigits: 20,
    });
    const percentformatter = Intl.NumberFormat(i18n.resolvedLanguage, { style: "percent" })
    const percentformatterfloat = Intl.NumberFormat(i18n.resolvedLanguage, { style: "percent", maximumFractionDigits: 2 })
    const currencyformatter = Intl.NumberFormat(i18n.resolvedLanguage, {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 5,
        useGrouping: 'always',
    })
    const currencyformattershortdigits = Intl.NumberFormat(i18n.resolvedLanguage, {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 2,
        useGrouping: 'always',
    })
    const currencyformatternodigits = Intl.NumberFormat(i18n.resolvedLanguage, {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
        useGrouping: 'always',
    })
    const compactCurrencyFormatter = (value, decimaldigits = 'full') => {
        const normalizedValue = normalizeNumericValue(value);
        const safeValue = normalizedValue ?? value;
        const absValue = Math.abs(safeValue);
        const compactFractionDigits = decimaldigits == 'none' ? 0 : 1;
        let result = '';

        if (absValue >= 1_000_000) {
            result = (safeValue / 1_000_000).toFixed(compactFractionDigits) + 'M€';
        } else if (absValue >= 1_000) {
            result = (safeValue / 1_000).toFixed(compactFractionDigits) + 'K€';
        } else if (decimaldigits == 'none') {
            result = currencyformatternodigits.format(safeValue);
        } else if (decimaldigits == 'standard') {
            result = currencyformattershortdigits.format(safeValue);
        } else {
            result = currencyformatter.format(safeValue);
        }

        return result;
    }

    const byteFormatter = Intl.NumberFormat(i18n.resolvedLanguage, {
        notation: "compact",
        style: "unit",
        unit: "kilobyte",
        unitDisplay: "narrow",
    });

    const formatDate = (string) => {
        let formatted = '-';
        if (string) {
            try {
                formatted = dateformatter.format(new Date(string));
            } catch (e) {
                formatted = <em>!{JSON.stringify(string)} </em>;
            }
        }
        return formatted;
    }

    const formatDatetime = (string) => {
        let formatted = '-';
        if (string) {
            try {
                formatted = datetimeformatter.format(new Date(string));
            } catch (e) {
                formatted = <em>!{JSON.stringify(string)} </em>;
            }
        }
        return formatted;
    }

    const formatDateForInput = (string) => {
        let formatted = '';
        if (string) {
            try {
                formatted = new Date(string).toLocaleDateString('en-CA');
            } catch (e) {
                // do nothing
            }
        }
        return formatted;
    }

    const formatDatetimeForInput = (string) => {
        let formatted = '';
        if (string) {
            try {
                const dateObj = new Date(string);

                const parts = datetimeformatter.formatToParts(dateObj);

                // Ricostruisci la data nel formato YYYY-MM-DDTHH:MM
                const year = parts.find(part => part.type === 'year').value;
                const month = parts.find(part => part.type === 'month').value;
                const day = parts.find(part => part.type === 'day').value;
                const hour = parts.find(part => part.type === 'hour').value;
                const minute = parts.find(part => part.type === 'minute').value;

                // Formatta la data per datetime-local (senza secondi e millisecondi)
                formatted = `${year}-${month}-${day}T${hour}:${minute}`;
            } catch (e) {
                // do nothing
            }
        }

        return formatted;
    }

    const formatDistance = (string) => {

        let formatted = '0 km';
        if (typeof string != typeof undefined && string !== null && !isNaN(string)) {
            try {
                formatted = distanceformatter.format(string);
            } catch (e) {
                formatted = string
            }
        }
        
        return formatted;
    }

    const normalizeNumericValue = (value) => {
        if (typeof value === 'number') return Number.isFinite(value) ? value : null;
        if (typeof value !== 'string') return null;

        const trimmedValue = value.trim();
        if (trimmedValue === '') return null;

        const normalizedValue = trimmedValue
            .replace(/\s/g, '')
            .replace(/[^\d,.-]/g, '');

        if (normalizedValue === '' || normalizedValue === '-' || normalizedValue === ',' || normalizedValue === '.') {
            return null;
        }

        const lastCommaIndex = normalizedValue.lastIndexOf(',');
        const lastDotIndex = normalizedValue.lastIndexOf('.');
        const decimalSeparatorIndex = Math.max(lastCommaIndex, lastDotIndex);

        let integerPart = normalizedValue;
        let decimalPart = '';

        if (decimalSeparatorIndex !== -1) {
            integerPart = normalizedValue.slice(0, decimalSeparatorIndex);
            decimalPart = normalizedValue.slice(decimalSeparatorIndex + 1);
        }

        const sanitizedIntegerPart = integerPart.replace(/[.,]/g, '');
        const sanitizedDecimalPart = decimalPart.replace(/[.,]/g, '');
        const numberAsString = sanitizedDecimalPart !== ''
            ? `${sanitizedIntegerPart}.${sanitizedDecimalPart}`
            : sanitizedIntegerPart;

        const parsedValue = Number(numberAsString);
        return Number.isFinite(parsedValue) ? parsedValue : null;
    };

    const formatNumber = (string) => {
        let formatted = '';
        if (typeof string != typeof undefined && string !== null) {
            try {
                const normalizedNumber = normalizeNumericValue(string);
                formatted = numberformatter.format(normalizedNumber ?? string);
            } catch (e) {
                formatted = string
            }
        }
        return formatted;
    }

    const formatPercentage = (string, float = false) => {
        let formatted = '';
        if (typeof string != typeof undefined && string !== null) {
            try {
                if (float) {
                    formatted = percentformatterfloat.format(string);
                } else {
                    formatted = percentformatter.format(string);
                }
            } catch (e) {
                formatted = string
            }
        }
        return formatted;
    }

    const formatCurrency = (string, compact = false, decimaldigits = 'full') => {
        let formatted = '';
        if (typeof string != typeof undefined && string !== null) {
            try {
                const normalizedNumber = normalizeNumericValue(string);
                const safeValue = normalizedNumber ?? string;
                if (compact) {
                    formatted = compactCurrencyFormatter(safeValue, decimaldigits);
                } else if (decimaldigits == 'none') {
                    formatted = currencyformatternodigits.format(safeValue);
                } else if (decimaldigits == 'standard') {
                    formatted = currencyformattershortdigits.format(safeValue);
                } else {
                    formatted = currencyformatter.format(safeValue);
                }
            } catch (e) {
                formatted = string
            }
        }
        
        return formatted;
    }

    const formatCurrencyVariable = (string, currency) => {
        let cf;
        switch (currency) {
            case 'EUR':
                cf = currencyformatter;
                break;
            case 'USD':
                cf = Intl.NumberFormat(i18n.resolvedLanguage, { style: 'currency', currency: 'USD', useGrouping: 'always' });
                break;
            case 'GBP':
                cf = Intl.NumberFormat(i18n.resolvedLanguage, { style: 'currency', currency: 'GBP', useGrouping: 'always' });
                break;
            case 'CHF':
                cf = Intl.NumberFormat(i18n.resolvedLanguage, { style: 'currency', currency: 'CHF', useGrouping: 'always' });
                break;
        }

        let formatted = '';
        if (typeof string != typeof undefined && string !== null) {
            try {
                const normalizedNumber = normalizeNumericValue(string);
                formatted = cf.format(normalizedNumber ?? string);
            } catch (e) {
                formatted = string
            }
        }
        return formatted;
    }

    const formatBytes = (string) => {
        let formatted = '';
        if (typeof string != typeof undefined && string !== null) {
            try {
                formatted = byteFormatter.format(string);
            } catch (e) {
                formatted = string
            }
        }
        return formatted;
    }

    return {
        formatDate,
        formatDatetime,
        formatDateForInput,
        formatDatetimeForInput,
        formatDistance,
        formatNumber,
        formatPercentage,
        formatCurrency,
        formatCurrencyVariable,
        formatBytes
    }

};
